import { useEffect, useMemo, useState } from 'react';
import { X, Shuffle, RotateCcw, Sparkles, Hand, BarChart3, Check } from 'lucide-react';
import { CardImage } from '@/components/CardImage';
import type { DeckCard } from '@/hooks/useDecks';
import type { InkColor } from '@/lib/ink-utils';

interface PlaytestSheetProps {
  open: boolean;
  onClose: () => void;
  cards: DeckCard[];
  deckName?: string;
}

type Drawn = DeckCard & { _instance: string };

function expand(cards: DeckCard[]): Drawn[] {
  const pool: Drawn[] = [];
  for (const dc of cards) {
    for (let i = 0; i < dc.quantity; i++) {
      pool.push({ ...dc, _instance: `${dc.card_id}-${i}` });
    }
  }
  return pool;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

type RunStat = {
  inkable: number;
  totalCost: number;
  avgCost: number;
  mulliganCount: number; // 0 = kept opener, 1 = altered once, etc.
  cardsSwapped: number; // total cards put back across mulligans
  kept: boolean;
};

export function PlaytestSheet({ open, onClose, cards, deckName }: PlaytestSheetProps) {
  const pool = useMemo(() => expand(cards), [cards]);
  const [hand, setHand] = useState<Drawn[]>([]);
  const [deck, setDeck] = useState<Drawn[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mulliganCount, setMulliganCount] = useState(0);
  const [cardsSwapped, setCardsSwapped] = useState(0);
  const [runs, setRuns] = useState<RunStat[]>([]);
  const [draws, setDraws] = useState<Drawn[]>([]);
  const [strict, setStrict] = useState(true); // one-mulligan rule vs. free re-shuffles

  const dealHand = () => {
    const shuffled = shuffle(pool);
    setHand(shuffled.slice(0, 7));
    setDeck(shuffled.slice(7));
    setSelected(new Set());
    setMulliganCount(0);
    setCardsSwapped(0);
    setDraws([]);
  };

  useEffect(() => {
    if (open && pool.length >= 7) dealHand();
    if (!open) {
      setHand([]);
      setDeck([]);
      setDraws([]);
      setSelected(new Set());
      setMulliganCount(0);
      setCardsSwapped(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleSelect = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const alterSelected = () => {
    if (strict && mulliganCount >= 1) return;
    if (selected.size === 0) return;
    // Put selected cards on the bottom of the deck (shuffled), draw replacements.
    const keeping = hand.filter((c) => !selected.has(c._instance));
    const returned = hand.filter((c) => selected.has(c._instance));
    const shuffledBottom = shuffle([...deck, ...returned]);
    const draw = shuffledBottom.slice(0, returned.length);
    const remaining = shuffledBottom.slice(returned.length);
    setHand([...keeping, ...draw]);
    setDeck(remaining);
    setCardsSwapped((n) => n + returned.length);
    setMulliganCount((n) => n + 1);
    setSelected(new Set());
    setDraws([]);
  };

  const alterAll = () => {
    if (strict && mulliganCount >= 1) return;
    const shuffled = shuffle([...hand, ...deck]);
    setHand(shuffled.slice(0, 7));
    setDeck(shuffled.slice(7));
    setCardsSwapped((n) => n + 7);
    setMulliganCount((n) => n + 1);
    setSelected(new Set());
    setDraws([]);
  };

  const drawOne = () => {
    if (!deck.length) return;
    const [next, ...rest] = deck;
    setDraws((d) => [...d, next]);
    setDeck(rest);
  };

  const inkableCount = hand.filter((c) => c.card?.inkable).length;
  const avgCost =
    hand.length === 0
      ? 0
      : hand.reduce((s, c) => s + (c.card?.cost || 0), 0) / hand.length;

  const keepRun = () => {
    setRuns((r) => [
      ...r,
      {
        inkable: inkableCount,
        totalCost: hand.reduce((s, c) => s + (c.card?.cost || 0), 0),
        avgCost,
        mulliganCount,
        cardsSwapped,
        kept: true,
      },
    ]);
    dealHand();
  };

  const summary = useMemo(() => {
    if (!runs.length) return null;
    const total = runs.length;
    const avgInk = runs.reduce((s, r) => s + r.inkable, 0) / total;
    const avgCo = runs.reduce((s, r) => s + r.avgCost, 0) / total;
    const mulPct = (runs.filter((r) => r.mulliganCount > 0).length / total) * 100;
    const avgSwaps = runs.reduce((s, r) => s + r.cardsSwapped, 0) / total;
    const screwed = runs.filter((r) => r.inkable < 3).length;
    return { total, avgInk, avgCo, mulPct, avgSwaps, screwed };
  }, [runs]);

  if (!open) return null;

  const tooSmall = pool.length < 7;
  const disableAlter = strict ? mulliganCount >= 1 : false;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="animate-slide-up w-full max-w-[430px] max-h-[92vh] flex flex-col rounded-t-2xl border border-border bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <Hand className="h-4 w-4 text-primary shrink-0" />
            <h3 className="font-display text-base font-semibold text-foreground truncate">
              Playtest{deckName ? ` · ${deckName}` : ''}
            </h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {tooSmall ? (
          <div className="p-8 text-center">
            <p className="text-3xl mb-2">🃏</p>
            <p className="text-sm text-muted-foreground font-body">
              Add at least 7 cards to playtest a hand.
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Mode switch */}
            <div className="flex items-center justify-between px-4 pt-3">
              <div className="flex gap-1 rounded-lg bg-secondary p-0.5">
                <button
                  onClick={() => setStrict(true)}
                  className={`rounded-md px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${strict ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
                >
                  Ranked (1 alter)
                </button>
                <button
                  onClick={() => setStrict(false)}
                  className={`rounded-md px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${!strict ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
                >
                  Free practice
                </button>
              </div>
              <span className="text-[10px] text-muted-foreground font-body">
                Alters: {mulliganCount} · swaps {cardsSwapped}
              </span>
            </div>

            {/* Hand */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-body">
                  Opening hand ({hand.length})
                </p>
                <div className="flex items-center gap-2 text-[10px] font-body">
                  <span className={inkableCount >= 3 ? 'text-ink-emerald' : 'text-ink-ruby'}>
                    {inkableCount} inkable
                  </span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">avg {avgCost.toFixed(1)}⬡</span>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground font-body mb-2">
                Tap cards to select, then bottom + redraw only those.
              </p>
              <div className="grid grid-cols-7 gap-1">
                {hand.map((c) => {
                  const isSel = selected.has(c._instance);
                  return (
                    <button
                      key={c._instance}
                      onClick={() => toggleSelect(c._instance)}
                      className={`relative aspect-[2/3] rounded overflow-hidden border-2 transition-all ${isSel ? 'border-primary scale-95 opacity-70' : 'border-transparent'}`}
                      title={c.card?.name}
                      aria-pressed={isSel}
                    >
                      <CardImage
                        src={c.card?.image_url}
                        alt={c.card?.name || ''}
                        inkColor={c.card?.ink_color as InkColor}
                        className="w-full h-full"
                      />
                      {isSel && (
                        <span className="absolute inset-0 flex items-center justify-center bg-primary/30">
                          <Check className="h-4 w-4 text-primary-foreground" />
                        </span>
                      )}
                      {!c.card?.inkable && !isSel && (
                        <span className="absolute top-0 right-0 m-0.5 h-1.5 w-1.5 rounded-full bg-ink-ruby ring-1 ring-card" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Subsequent draws */}
              {draws.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body mb-1">
                    Drawn after ({draws.length})
                  </p>
                  <div className="grid grid-cols-7 gap-1">
                    {draws.map((c) => (
                      <div
                        key={c._instance}
                        className="aspect-[2/3] rounded overflow-hidden opacity-90"
                      >
                        <CardImage
                          src={c.card?.image_url}
                          alt={c.card?.name || ''}
                          inkColor={c.card?.ink_color as InkColor}
                          className="w-full h-full"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Controls */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  onClick={alterSelected}
                  disabled={disableAlter || selected.size === 0}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-primary/10 px-3 py-2 text-xs font-medium text-primary font-body hover:bg-primary/20 disabled:opacity-40"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Bottom {selected.size || ''} & redraw
                </button>
                <button
                  onClick={alterAll}
                  disabled={disableAlter}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-xs font-medium text-foreground font-body hover:bg-accent disabled:opacity-40"
                >
                  <Shuffle className="h-3.5 w-3.5" /> Alter all 7
                </button>
                <button
                  onClick={keepRun}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground font-body"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Keep & log
                </button>
                <button
                  onClick={drawOne}
                  disabled={!deck.length}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-xs font-medium text-foreground font-body hover:bg-accent disabled:opacity-40"
                >
                  Draw 1 ({deck.length})
                </button>
                <button
                  onClick={dealHand}
                  className="col-span-2 flex items-center justify-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-[11px] font-medium text-muted-foreground font-body hover:bg-accent"
                >
                  <Shuffle className="h-3.5 w-3.5" /> New shuffle (skip logging)
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                <h4 className="font-display text-sm font-semibold text-foreground">
                  Run stats ({runs.length})
                </h4>
                {runs.length > 0 && (
                  <button
                    onClick={() => setRuns([])}
                    className="ml-auto text-[10px] text-muted-foreground hover:text-ink-ruby font-body"
                  >
                    Reset
                  </button>
                )}
              </div>
              {!summary ? (
                <p className="text-[11px] text-muted-foreground font-body">
                  Keep a hand to start logging stats. Aim for ≥3 inkable cards to avoid an ink screw.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2 text-xs font-body">
                  <div className="rounded-lg bg-secondary/60 p-2.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Avg inkable
                    </p>
                    <p className={`font-semibold ${summary.avgInk >= 3 ? 'text-ink-emerald' : 'text-ink-ruby'}`}>
                      {summary.avgInk.toFixed(2)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-secondary/60 p-2.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg cost</p>
                    <p className="font-semibold text-foreground">{summary.avgCo.toFixed(2)}⬡</p>
                  </div>
                  <div className="rounded-lg bg-secondary/60 p-2.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Mulligan rate</p>
                    <p className="font-semibold text-foreground">{summary.mulPct.toFixed(0)}%</p>
                  </div>
                  <div className="rounded-lg bg-secondary/60 p-2.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg cards swapped</p>
                    <p className="font-semibold text-foreground">{summary.avgSwaps.toFixed(1)}</p>
                  </div>
                  <div className="col-span-2 rounded-lg bg-secondary/60 p-2.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Ink screws (&lt;3 inkable)</p>
                    <p className={`font-semibold ${summary.screwed / summary.total > 0.2 ? 'text-ink-ruby' : 'text-foreground'}`}>
                      {summary.screwed}/{summary.total}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
